import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowDownTrayIcon, ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import { Article } from '../data/articles';
import { fetchArticle } from '../api/articles';
import { dataFileUrl } from '../constant/hostname';
import Header from './Header';
import Footer from './Footer';

const formatDate = (date: string) =>
  `${date.slice(0, 4)}/${date.slice(4, 6)}/${date.slice(6, 8)}`;

const ArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'error' | 'notfound' | 'ready'>('loading');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!id) {
      setLoadState('notfound');
      return;
    }
    let cancelled = false;
    setLoadState('loading');
    fetchArticle(id)
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setArticle(null);
          setLoadState('notfound');
          return;
        }
        setArticle(data);
        setLoadState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loadState === 'loading') {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#fafaf7] flex items-center justify-center pt-20">
          <div className="text-center text-gray-400 flex flex-col items-center gap-3">
            <ArrowPathIcon className="h-6 w-6 animate-spin text-[#4a7c59]" />
            <p className="text-base">記事を読み込んでいます...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (loadState === 'error') {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#fafaf7] flex items-center justify-center pt-20">
          <div className="text-center">
            <p className="text-gray-500 mb-4">記事を読み込めませんでした。</p>
            <Link to="/" className="text-[#1a4a2e] underline text-sm hover:text-[#4a7c59]">
              トップに戻る
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (loadState === 'notfound' || !article) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#fafaf7] flex items-center justify-center pt-20">
          <div className="text-center">
            <p className="text-gray-500 mb-4">記事が見つかりませんでした。</p>
            <Link to="/" className="text-[#1a4a2e] underline text-sm hover:text-[#4a7c59]">
              トップに戻る
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const handleShare = async () => {
    try {
      await navigator.share({ title: article.title, text: article.title, url: window.location.href });
    } catch {
      // キャンセル時は何もしない
    }
  };

  const pdfHref = dataFileUrl(article.url);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#fafaf7] pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* 戻るボタン */}
          <button
            onClick={() => navigate('/', { state: { scrollTo: 'articles' } })}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#1a4a2e] transition mb-6 mt-4"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            記事一覧に戻る
          </button>

          {/* 記事カード */}
          <article className="bg-white rounded-2xl p-6 sm:p-10 border border-gray-200/60 shadow-sm">
            {/* メタ情報 */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="text-xs text-gray-400">{formatDate(article.createdAt)}</span>
              <span className="text-xs font-semibold text-[#4a7c59] bg-[#4a7c59]/10 px-2.5 py-0.5 rounded-full">
                {article.author}
              </span>
            </div>

            {/* タイトル */}
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-6">
              {article.title}
            </h1>

            {/* 概要 */}
            <div className="bg-[#f5f0e8] rounded-xl p-4 sm:p-5 mb-8 border-l-4 border-[#1a4a2e]">
              <p className="text-xs font-bold text-[#1a4a2e] mb-2 uppercase tracking-wide">
                概要
              </p>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {article.abstract}
              </p>
            </div>

            {/* PDFダウンロード＋シェア */}
            <div className="flex flex-wrap items-center gap-3 mb-10">
              {pdfHref && (
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#1a4a2e] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#4a7c59] transition shadow"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  詳細を確認
                </a>
              )}
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 border border-gray-300 text-gray-600 text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gray-100 transition"
                aria-label="この記事をシェア"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                シェア
              </button>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ArticlePage;
